
import measurementTypes from './MeasurementTypes'

const measurementUtil = {

    toHHMMSS: function(secs) {
        if(secs < 60)
            return '' + secs;

        let sec_num = parseInt(secs, 10)
        let hours   = Math.floor(sec_num / 3600)
        let minutes = Math.floor(sec_num / 60) % 60
        let seconds = sec_num % 60

        return [hours,minutes,seconds]
            .map(v => v < 10 ? "0" + v : v)
            .filter((v,i) => v !== "00" || i > 0)
            .join(":")
    },

    roundWithDecimals: function(value, decimals) {
        if(!value)
            return 0;
        if(!decimals || decimals <= 0)
            return Math.round(value);

        let result = Number(Math.round(value+'e'+decimals)+'e-'+decimals);
        if(!result || isNaN(result))
            return 0;

        return result;
    },

    canAcceptZeroValue: function(measurementType) {

        switch(measurementType.id ?? measurementType) {
            case (measurementTypes.RespiratoryFrequency):
            case (measurementTypes.TidalVolume):
            case (measurementTypes.Ventilation):
            case (measurementTypes.FractionOfExpiredOxygen):
            case (measurementTypes.FractionOfExpiredCO2):
            case (measurementTypes.OxygenConsumption):
            case (measurementTypes.OxygenConsumptionByWeight):
            case (measurementTypes.CarbonDioxideProduction):
            case (measurementTypes.CarbonDioxideProductionByWeight):
            case (measurementTypes.RespiratoryExchangeRatio):
            case (measurementTypes.AmbientO2):
            case (measurementTypes.AmbientCO2):
            case (measurementTypes.AmbientPressure):
            case (measurementTypes.VentilationVO2):
            case (measurementTypes.HeartRate):
            case (measurementTypes.HeartRRInterval):
            case (measurementTypes.SmO2):
            case (measurementTypes.THb):
                return false;
            default:
                return true;
        }
    },

    filterDuplicates: function( dataPoints, decimals) {

        let result = [];

        let measurementsCount = dataPoints.length;
        if (measurementsCount < 1)
            return result;

        let formatter = Intl.NumberFormat('en', { maximumFractionDigits: decimals });
        if(decimals === undefined)
            decimals = 2;

        let prev = null;
        //let prevTime = null;
        for (let i=0; i < measurementsCount; i++) {

            let current = dataPoints[i];
            let timeMark = Math.floor(current.time);
            let value = current.value;
            let roundedVal = this.roundWithDecimals(value, decimals);

            if(prev) {
                if( (Math.abs(prev - roundedVal)) < Number.EPSILON) {
                    continue;
                }

                result.push({time: timeMark, value: prev, valueFormatted: formatter.format(prev)});
            }

            result.push({time: timeMark, value: roundedVal, valueFormatted: formatter.format(roundedVal)});
            prev = roundedVal;
            //prevTime = timeMark;
        }

        //add final point to align time values
        let last = dataPoints[measurementsCount-1];
        let lastTime = Math.floor(last.time);
        let roundedVal = this.roundWithDecimals(last.value, decimals);
        result.push({time: lastTime, value: roundedVal, valueFormatted: formatter.format(roundedVal)});

        return result;
    },

    calculateAverage: function( dataPoints,  interval, maxTime, decimals) {

        let result = [];

        let formatter = Intl.NumberFormat('en', { maximumFractionDigits: decimals });
        if(decimals === undefined)
            decimals = 2;

        let measurementsCount = dataPoints.length;
        if (measurementsCount < 1)
            return result; //ignore empty measurement data sets


        let lastMeasurementCutOff = Math.max(interval * 1.5, 15);

        let measurementsIndex = 0;
        let timeOffset = 0;
        let lastMeasurement = null;



        //increment the timeOffset by the interval, collect all values in that range and record the average
        while (timeOffset <= maxTime)
        {
            let timeStart = timeOffset;
            timeOffset += interval;
            let isLastRow = timeOffset + interval / 2 > maxTime;

            //set time value
            let timeMark = Math.round(timeOffset);

            let averageCount = 0;
            let averageVal = 0;
            let validData = false;

            {
                //collect data points in time range  timeStart < val <= (timeStart + interval)
                let i = measurementsIndex;
                for (; i < measurementsCount; i++)
                {
                    let current = dataPoints[i];
                    let t = Math.floor(current.time);
                    if (t > timeStart)
                    {
                        if (t <= timeOffset)
                        {
                            averageCount++;
                            averageVal += current.value;
                            lastMeasurement = current;
                            validData = true;
                        }
                        else
                        {
                            break;
                        }
                    }
                }

                if (validData)
                {
                    averageVal = this.roundWithDecimals(averageVal / averageCount, decimals);
                    measurementsIndex = i;
                }
            }

            //If no current data found, try to use the latest.
            if (!validData && result.length > 0)
            {
                //if we haven't got a measurement within a short time, insert zero values into the averaged data stream,
                //Otherwise use latest data point.

                let isUpcomingData = false;
                let i = measurementsIndex;
                for (; i < measurementsCount; i++)
                {
                    let current = dataPoints[i];
                    let t = Math.floor(current.time);
                    if (t > timeStart)
                    {
                        if (t <= timeOffset + lastMeasurementCutOff)
                        {
                            isUpcomingData = true;
                        }
                        else
                        {
                            break;
                        }
                    }
                }

                if (isUpcomingData)
                {
                    let lastTime = lastMeasurement != null ? lastMeasurement.time : 0;
                    lastTime = Math.floor(lastTime);
                    let lastValue = lastMeasurement != null ? lastMeasurement.value : 0;
                    if (timeMark - lastTime < lastMeasurementCutOff) {
                        averageVal = lastValue;
                        validData = true;
                    }
                }
            }

            if (isLastRow && Math.abs(averageVal) < Number.EPSILON) {
                continue;
            }

            let roundedVal = this.roundWithDecimals(averageVal, decimals);
            result.push( {time: timeMark, value: roundedVal, valueFormatted: formatter.format(roundedVal), isReal: validData });
        }

        return result;
    },

    calculateMetrics: function(dataPoints, measurementType) {

        let canAcceptZero = true;
        if(measurementType) {
            canAcceptZero = this.canAcceptZeroValue(measurementType);
        }

        let min = Number.MAX_VALUE; let max = Number.MIN_VALUE; let avg = 0; let count = 0; let last = 0;
        for(let i=0; i<dataPoints.length;i++) {

            let pt = dataPoints[i];
            if(pt.isReal !== undefined && !pt.isReal) {
                continue;
            }
            if (!canAcceptZero && Math.abs(pt.value) < Number.EPSILON) {
                continue;
            }

            if (pt.value > max)
                max = pt.value;
            if (pt.value < min)
                min = pt.value;
            avg += pt.value;
            count++;
        }

        if (count > 0)
        {
            avg /= count;
            last = dataPoints[dataPoints.length-1].value;
        }
        else
        {
            min = max = avg = last = 0;
        }

        let decimals = 2;
        return {
            last: this.roundWithDecimals(last , decimals),
            min: this.roundWithDecimals(min , decimals),
            max: this.roundWithDecimals(max , decimals),
            average: this.roundWithDecimals(avg , decimals)
        };
    },


    calculateProtocolPhases: function(protocolDataPoints, sessionDurationSeconds) {

        let points = protocolDataPoints;

        let result = {
            steps: [],
            phases: []
        };

        if(!points || points.length < 1 || !sessionDurationSeconds)
            return result;

        //convert points to steps
        for(let i=0;i<points.length;i++) {
            let current = points[i];
            let currentTime = current.time / 1000; //ms to sec

            let nextStepStartTime = sessionDurationSeconds;
            if (i < points.length - 1)
                nextStepStartTime = points[i + 1].time / 1000; //ms to sec

            let duration = Math.max(0, nextStepStartTime - currentTime);
            result.steps.push({
                phaseId: current.phaseId,
                phaseType: current.phaseType,
                stepType: current.stepType,
                expectedDuration: current.expectedDuration / 1000, //ms to sec
                stepNumber: current.stepNumber,

                time: currentTime,
                duration: duration,
                endTime: currentTime + duration,
                value: current.value,
            });
        }

        if(result.steps.length > 0) {

            //convert steps to phases
            let phaseType = result.steps[0].phaseType;
            let phaseDuration = 0;
            let phaseStartTime = 0;
            let didAddLastStep = false;

            let phaseSteps = [];

            for (let i = 0; i < result.steps.length; i++) {

                let current = result.steps[i];

                if (current.phaseType == phaseType) {
                    phaseDuration += current.duration;
                    phaseSteps.push(current);
                } else {

                    //Add phase.
                    result.phases.push({
                        startTime: phaseStartTime,
                        duration: phaseDuration,
                        phaseType: phaseType,
                        steps: phaseSteps
                    });

                    //Change vars for next phase.
                    phaseType = current.phaseType;
                    phaseDuration = current.duration;
                    phaseStartTime = current.time;
                    phaseSteps = [current];

                    //Ensure the last phase is drawn, even if it's on its own.
                    if (i == result.steps.length - 1) {
                        didAddLastStep = true;
                        result.phases.push({
                            startTime: phaseStartTime,
                            duration: phaseDuration,
                            phaseType: phaseType,
                            steps: phaseSteps
                        });
                    }
                }
            }

            if (!didAddLastStep) {
                result.phases.push({
                    startTime: phaseStartTime,
                    duration: phaseDuration,
                    phaseType: phaseType,
                    steps: phaseSteps
                });
            }

        }

        return result;
    }

};

export default measurementUtil